import { KeyboardEvent, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import tw from 'twin.macro';

const InputWrapper = tw.div`flex px-[8px] py-[4px] max-w-[500px] items-center align-middle border border-solid border-gray-1 pr-2 rounded cursor-pointer shadow-dropdown bg-neutral-background  focus-within:outline-none focus-within:border-accent-muted gap-[12px]`;
const Input = tw.div`text-text-and-icon text-[11px] font-medium whitespace-nowrap empty:before:(text-text-and-icon-muted-2 opacity-50) focus-visible:outline-none min-w-[20px]`;
const Button = tw.div`flex items-center justify-center rounded-[3px] bg-page-background text-[8px] text-text-and-icon font-semibold px-[4px] py-[3px]`;

type FilterInputProps = {
  value?: string;
  placeholder: string;
  onPressEnter: (value: string | number) => void;
  contentEditable?: boolean;
  type?: 'text' | 'number';
  focus?: boolean;
};

export const FilterInput = ({
  value: defaultValue = '',
  focus,
  contentEditable,
  placeholder,
  onPressEnter,
  type = 'text',
  ...props
}: FilterInputProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLDivElement>(null);

  const handleKeyPress = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();

      const value =
        type === 'number'
          ? Number((e.target as HTMLDivElement).innerText)
          : (e.target as HTMLDivElement).innerText;
      onPressEnter(value);
    }

    if (type === 'number' && e.key !== 'Backspace' && e.key !== 'Delete') {
      const isNumber = /^\d+$/.test(e.key);
      if (!isNumber) {
        e.preventDefault();
        e.stopPropagation();
      }
    }
  };

  const handleClickEnter = useCallback(() => {
    if (!inputRef.current) return;

    if (!inputRef.current.innerText) {
      inputRef.current.focus();
      return;
    }

    const value =
      type === 'number' ? Number(inputRef.current.innerText) : inputRef.current.innerText;
    onPressEnter(value);
  }, [inputRef.current]);

  const onBlur = useCallback(() => {
    if (!inputRef.current) return;

    const value =
      type === 'number' ? Number(inputRef.current.innerText) : inputRef.current.innerText;
    onPressEnter(value);
  }, [inputRef.current]);

  useEffect(() => {
    if (focus) {
      inputRef.current?.focus();
    }

    if (defaultValue && inputRef.current) {
      inputRef.current.innerText = defaultValue;
    }
  }, []);

  return (
    <InputWrapper>
      <Input
        className='filter-input'
        ref={inputRef}
        role='textbox'
        contentEditable={contentEditable}
        css={{
          cursor: contentEditable ? 'text' : 'auto',
          ':empty::before': { content: `"${placeholder}"` },
        }}
        onKeyDown={(e) => handleKeyPress(e as KeyboardEvent<HTMLDivElement>)}
        onBlur={onBlur}
        {...props}
      />
      <Button onClick={handleClickEnter}>{t('common.enter')}</Button>
    </InputWrapper>
  );
};

FilterInput.displayName = 'FilterInput';
